import { ComponentChildren } from 'preact';
import { MutableRef, useRef } from 'preact/hooks';

import { NavigationListContext } from './NavigationListContext';
import { navigationListStyles } from './themes/NavigationListStyles.css';
import { useNavigationList } from './useNavigationList';

/**
 * type for payload of selection change event handler
 */
type SelectionDetail<K> = {
  value: K;
};

export type NavigationListProps<K extends string | number> = {
  /**
   * A set of NavigationListItem(s) that NavigationList will hold
   */
  children: ComponentChildren;
  /**
   * Key of the selected item.
   */
  selection?: K;
  /**
   * Callback function to handle when selection has changed
   */
  onSelectionChange?: <K extends string | number>(detail: SelectionDetail<K>) => void;
  /**
   * Specifies some screen reader text. Set it to create an accesible navigationList.
   */
  'aria-label'?: string;

  /**
   * Specifies some a relation between navigationList and other element. Based on this specifies some
   * screen reader text. Set it to create an accesible navigationList.
   */
  'aria-labelledby'?: string;
};

export function NavigationList<K extends string | number>({
  children,
  selection,
  onSelectionChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelLedBy
}: NavigationListProps<K>) {
  const containerRef = useRef<HTMLUListElement>(null);

  const {
    firstItemRenderedKey,
    showFocusRing,
    currentKey,
    onCurrentKeyChange,
    containsActiveElement,
    containerHandlers
  } = useNavigationList({
    containerRef: containerRef as MutableRef<HTMLUListElement>,
    selection
  });

  return (
    <ul
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelLedBy}
      role="listbox"
      ref={containerRef}
      tabIndex={children ? -1 : 0}
      {...containerHandlers}
      class={navigationListStyles.uListStyle}>
      <NavigationListContext.Provider
        value={{
          selection,
          onSelectionChange,
          onCurrentKeyChange,
          currentKey,
          showFocusRing,
          containsActiveElement,
          firstItemRenderedKey
        }}>
        {children}
      </NavigationListContext.Provider>
    </ul>
  );
}
